import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import { setPageTitle } from 'common/hooks/usePageTitle';
import styled, { withTheme } from 'styled-components';
import { withRouter } from 'react-router-dom';
import { history } from 'common/helpers/history';
import { toastr } from 'react-redux-toastr';
import * as _ from 'lodash';

import AccountMenuItems from 'common/components/organisms/AccountMenuItems';
import MoreOptions from 'common/components/organisms/MoreOptions';
import MsgContainer from 'common/components/atoms/MsgContainer';
import FilterCountContainer from 'common/components/atoms/FilterCountContainer';
import { generateQueryString, isJsonString, calendarFormatDay } from 'common/utils';
import { getSensorTypeDisplayName } from 'common/utils/sensorTypeDisplayName';
import { Routes } from 'home/HomePage/constants/routes.constants';
import SearchFilters from 'common/components/atoms/SearchFilters';
import FooterFlex_T from 'common/components/atoms/FooterFlex';
import Label from 'common/typography/Label/Label';
import UnassignSensorAlertPrompt from 'common/components/organisms/AlertPrompt';
import colors from 'common/styles/colors';
import EditIcon from 'common/images/BearingModal/EditIcon';
import { widgetActions } from 'home/Widget/actions/widget.actions';
import Loading from 'common/components/atoms/Loading';
import FilterBar from 'common/components/organisms/FilterBar';
import { isPartnerAdmin } from 'common/rbac/util';
import AssignModel from './AssignToAccountModel';
import AddDeviceModel from './AddDeviceModal';
import DevicesInsights from './DevicesInsights';

import * as modelActions from '../../Settings/SensorModels/actions/models.actions';
import * as machineDetailsActions from '../../Machines/MachineDetails/machineDetails.actions';
import * as sensorActions from '../actions/sensors.actions';
import InputField from '../../../common/components/atoms/InputField';
import { Plus, ColumnIcon } from '../../../common/images/FaIcons';
import {
  ToolbarSearchBox, GhostBtn, PrimaryLinkBtn, PrimaryButton
} from '../../../common/components/molecules/ListToolbar';
import DevicesTable from './DevicesTable';
import OutsideAlerter from '../../../common/OutsideAlerter';
import SensorsDetails from './SensorsDetails';

import Checkbox from '../../../common/components/atoms/Checkbox';
import FlexContainer from '../../../common/components/atoms/FlexContainer';
import { BatteryIcon } from '../../../common/images/BatteryIcon';
import WiFiSvg from '../../../common/images/WiFiSvg';
import Button from '../../../common/components/atoms/Button';
import RBAC from '../../../common/rbac/RBAC';
import {
  mapComponentToResource,
  operations
} from '../../../common/rbac/constants';
import Radiobutton from '../../../common/components/molecules/Radiobutton';
import Calendar from '../../../common/components/molecules/ReactCalendar';
import H2 from '../../../common/typography/H2/H2';
import CrossSvg from '../../../common/images/CrossSvg';
import {
  allSensorsPageColumns, defaultSensorsPageColumns, StandardSensors, VectorSensors
} from '../constants/sensors.constants';
import EditDropdown from './EditDropdown';
import { sensorsConstants } from '../actions/sensors.constants';

const FooterFlex = styled(FooterFlex_T)`
  align-self: flex-end;
`;

const FilterItem = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5em 1em;
  text-align: left;
  width: max-content;
  min-width: 150px;
  border: none;
  outline: none;
  background-color: white;
  font-family: 'Petasense Open Sans';
  font-weight: 600;
  color: ${props => props.theme.colors.lightGray};
  ${props => props.selected && `color: ${props.theme.colors.black};`}
  &:hover {
    color: ${props => props.theme.colors.black};
    background-color: #f7f7f7;
  }
  &:first-of-type {
    padding-top: 1em;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  &:last-of-type {
    padding-bottom: 1em;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  span {
    margin-left: 0.5em;
  }
`;

const OptionSearch = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e5e8e1;
  border-radius: 6px;
  padding: 5px 9px;
  margin: 4px 6px 8px;
  input {
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    width: 100%;
    color: #383838;
  }
`;

const DateSelector = styled.div`
  height: 100%;
  display: inline-block;
  width: 250px;
  vertical-align: top;
  padding: 10px 15px;
  box-sizing: border-box;
  ${InputField} {
    margin-bottom: 13.5px;
  }
  input {
    font-size: 1em;
    padding: .57857143em 1em;
  }
  label {
    font-weight: 600;
    margin-bottom: 3px;
  }
`;

const CalenderContainer = styled.div`
  position: absolute;
  z-index: 3;
  width: 220px;
  top: ${props => props.to ? '103px' : '33px'};
  right: 240px;
`;

const SensorsContainer = styled(FlexContainer).attrs({
  direction: 'row'
})`
  background-color: ${props => props.theme.colors.white};
  height: Calc(100vh - var(--title-bar-offset, 0px) - 172px);
  overflow: auto;
`;

const ListContainer = styled(FlexContainer).attrs({ direction: 'column' })`
  position: relative;
  overflow: auto;
  width: ${props => props.width};
  height: 100%;
  ${props =>
    props.width === '30%' &&
    `border-right: 2px solid ${props.theme.colors.greyL};`}
  ${props =>
    props.minWidth && `min-width: ${props.minWidth};`}
`;

const WifiDropdownText = ({ wifiLevel, text }) => (
  <Fragment>
    {wifiLevel !== null && <WiFiSvg wifiLevel={wifiLevel} />}
    <span style={{ marginLeft: '5px', marginTop: '-5px' }}>{text}</span>
  </Fragment>
);

const SvgContainer = styled(FlexContainer)`
  cursor: pointer;
  padding: 0.5em;
  padding-left: 2em;
`;

class Sensors extends Component {
  getSensorTypeLabel = (text) => {
    const { t, i18n } = this.props;
    return getSensorTypeDisplayName(t, i18n, text);
  };

  constructor(props) {
    super(props);
    this.initialSearchParams = {
      search_key: '',
      machine_search_key: '',
      area_search_key: '',
      account_search_key: '',
      firmware_search_key: '',
      type: [],
      machines: [],
      area: [],
      accounts: [],
      battery_level: [],
      wifi_signal: [],
      last_measurement: [],
      firmwares: [],
      last_measurement_from: '',
      last_measurement_to: '',
      unreachable_extended: '',
      devices_health_widget_filters: [],
      other_filters: []
    };
    this.state = {
      // selectedSerialNumber: null,
      selectedColumns: this.getPageColumns(props),
      machineSensors: false,
      machines: [],
      area: [],
      accounts: [],
      firmwares: [],
      allArea: [],
      allMachines: [],
      allAccounts: [],
      allFirmwares: [],
      editMode: false,
      calenderOpen: null,
      dateExpanded: false,
      customDayFrom: false,
      customDayTo: false,
      searchParams: {
        ...this.initialSearchParams,
      },
      filtersOpen: {
        type: false,
        machines: false,
        accounts: false,
        area: false,
        battery_level: false,
        wifi_signal: false,
        last_measurement: false,
        firmwares: false,
        other_filters: false
      },
      typeOptions: [],
      assignModelOpen: false,
      unassignModelOpen: false,
      addDeviceModelOpen: false,
      insightsOpen: false
    };
  }

  toggleInsights = () => {
    this.setState(prevState => ({ insightsOpen: !prevState.insightsOpen }));
  };

  getBatteryLevelOptions = () => {
    const { t } = this.props;
    return [
      { text: <BatteryIcon batteryLevel={3} />, value: 3, name: t('devices.filterOptions.batteryLevel.lessThan10') },
      { text: <BatteryIcon batteryLevel={2} />, value: 2, name: t('devices.filterOptions.batteryLevel.lessThan20') },
      { text: <BatteryIcon batteryLevel={1} />, value: 1, name: t('devices.filterOptions.batteryLevel.above20') },
      { text: <BatteryIcon batteryLevel={-1} />, value: null, name: t('devices.filterOptions.batteryLevel.unknown') }
    ];
  };

  getWifiSignalOptions = () => {
    const { t } = this.props;
    return [
      { text: <WifiDropdownText wifiLevel={0} text={t('devices.filterOptions.wifiSignal.veryWeak')} />, value: 0, name: t('devices.filterOptions.wifiSignal.veryWeak') },
      { text: <WifiDropdownText wifiLevel={1} text={t('devices.filterOptions.wifiSignal.weak')} />, value: 1, name: t('devices.filterOptions.wifiSignal.weak') },
      { text: <WifiDropdownText wifiLevel={2} text={t('devices.filterOptions.wifiSignal.normal')} />, value: 2, name: t('devices.filterOptions.wifiSignal.normal') },
      { text: <WifiDropdownText wifiLevel={3} text={t('devices.filterOptions.wifiSignal.good')} />, value: 3, name: t('devices.filterOptions.wifiSignal.good') },
      { text: <WifiDropdownText wifiLevel={4} text={t('devices.filterOptions.wifiSignal.strong')} />, value: 4, name: t('devices.filterOptions.wifiSignal.strong') },
      { text: <WifiDropdownText text={t('devices.filterOptions.wifiSignal.unknown')} />, value: null, name: t('devices.filterOptions.wifiSignal.unknown') }
    ];
  };

  getLastMeasurementOptions = () => {
    const { t } = this.props;
    return [
      { text: t('devices.filterOptions.lastMeasurement.allReachable'), value: false, name: t('devices.filterOptions.lastMeasurement.allReachable') },
      { text: t('devices.filterOptions.lastMeasurement.allUnreachable'), value: true, name: t('devices.filterOptions.lastMeasurement.allUnreachable') },
      { text: t('devices.filterOptions.lastMeasurement.allUnassigned'), value: 'unassigned', name: t('devices.filterOptions.lastMeasurement.allUnassigned') },
      { text: t('devices.filterOptions.lastMeasurement.allAssigned'), value: 'assigned', name: t('devices.filterOptions.lastMeasurement.allAssigned') },
      { text: t('devices.filterOptions.lastMeasurement.custom'), value: 'custom', name: t('devices.filterOptions.lastMeasurement.custom') },
      { text: t('devices.filterOptions.lastMeasurement.unreachableExtended'), value: 'unreachable_extended', name: t('devices.filterOptions.lastMeasurement.unreachableExtended') }
    ];
  };

  getOtherFiltersOptions = () => {
    const { t } = this.props;
    return [
      { text: t('devices.filterOptions.otherFilters.continuousBadData'), value: 'devices_with_continuous_outliers', name: t('devices.filterOptions.otherFilters.continuousBadData') },
      { text: t('devices.filterOptions.otherFilters.unevenVibrations'), value: 'devices_with_inconsistent_vibrations', name: t('devices.filterOptions.otherFilters.unevenVibrations') },
      { text: t('devices.filterOptions.otherFilters.zeroValues'), value: 'tx_sensors_with_zero_values', name: t('devices.filterOptions.otherFilters.zeroValues') }
    ];
  };

  componentDidUpdate(prevProps) {
    const { getResourcesBasedOnType } = this.props.sensorActions;
    if (this.props.adminDashboard.adminOpen !== prevProps.adminDashboard.adminOpen) {
      if (this.props.adminDashboard.adminOpen) {
        getResourcesBasedOnType('account').then((res) => {
          const accountsList = [{ text: this.props.t('sensors.accounts.unassociated'), value: null }, ...res];
          this.setState({
            accounts: accountsList,
            allAccounts: accountsList,
          });
        });
      }
      this.search();
    }
    if (!_.isEqual(this.props.location.search, prevProps.location.search)) {
      this.selectFiltersFromUrl();
    }
  }

  componentDidMount() {
    const title = this.props.title;
    setPageTitle({
      partnerTitle: title,
      pageTitle: this.props.t('pageTitles.devices')
    });

    this.mounted = true;
    const { machine_id } = this.props.match.params;
    const {
      getSensorsMetadata,
      getResourcesBasedOnType,
      setSensorsFilters
    } = this.props.sensorActions;
    let p1;
    getResourcesBasedOnType('mcu_firmware').then((res) => {
      this.setState({ firmwares: res, allFirmwares: res }, this.selectFilterFromCharts);
    }
    );
    if (machine_id) {
      this.setState({
        machineSensors: true
      });
      p1 = this.changeFilter('machines', [Number(machine_id)]);
    } else {
      getResourcesBasedOnType('machine').then(res =>
        this.setState({
          machines: res,
          allMachines: res
        })
      );
      if (this.props.adminDashboard.adminOpen) {
        getResourcesBasedOnType('account').then((res) => {
          let accountsList = [];
          if (isPartnerAdmin(this.props.user)) accountsList = [...res];
          else accountsList = [{ text: this.props.t('sensors.accounts.unassociated'), value: null }, ...res];
          this.setState({
            accounts: accountsList,
            allAccounts: accountsList,
          });
        });
      }
      getResourcesBasedOnType('area').then(res =>
        this.setState({ area: res, allArea: res })
      );
      if (this.props.location.state && this.props.location.state.filter) p1 = this.selectFilterFromCharts();
      else if (!_.isEmpty(this.props.location.search) && this.props.location.pathname === '/devices') {
        this.selectFiltersFromUrl();
      } else {
        const deviceListSettings = JSON.parse(localStorage.getItem('device_list_settings'));
        const filters = [
          { name: 'machine_id', op: 'in', value: [] },
          { name: 'account_id', op: 'in', value: [] },
          { name: 'battery_level', op: 'in', value: [] },
          { name: 'type', op: 'in', value: [] },
          { name: 'wifi_signal', op: 'in', value: [] },
          { name: 'area_id', op: 'in', value: [] },
          { name: 'unreachable', op: 'in', value: [] },
          { name: 'mcu_firmware', op: 'in', value: [] },
          { name: 'last_measurement_from', op: 'ge', value: '' },
          { name: 'last_measurement_to', op: 'le', value: '' },
          { name: 'unreachable_extended', op: 'eq', value: '' },
          { name: 'other_filters', op: 'in', value: [] }
        ];
        if (deviceListSettings) {
          const lastMeasurementFilter = deviceListSettings.last_measurement_filter;
          if (lastMeasurementFilter === 'assigned') {
            filters.push({ name: 'machine', op: 'ne', value: '' });
            this.setState(prevState => ({
              searchParams: {
                ...prevState.searchParams,
                last_measurement: ['assigned']
              }
            }));
          }
        }
        p1 = setSensorsFilters(filters, '');
      }
    }
    const p2 = getSensorsMetadata();
    const serial_number =
      this.props.location.state && this.props.location.state.serial_number;
    if (serial_number) {
      Promise.all([p1, p2]).then(() => {
        const { getSensorDetails } = this.props.sensorActions;
        getSensorDetails(serial_number, true);
      });
    }
    p2.then(() => {
      this.setState(prevState => ({
        typeOptions: prevState.typeOptions.concat(this.props.sensors.metadata.metadata.sensor_types)
      }));
    });

    this.props.widgetActions.getWidgetsMetadata();
  }


  componentWillUnmount() {
    this.closeDetails();
    this.mounted = false;
  }

  updateUrlQueryString = _.debounce(() => {
    if (this.props.location.pathname !== '/devices') return;
    const searchParams = _.cloneDeep(this.state.searchParams);
    if (_.isArray(searchParams.last_measurement) && searchParams.last_measurement[0] === 'custom') {
      searchParams.last_measurement = [];
    }
    const queryString = generateQueryString(
      searchParams,
      ['type', 'area', 'machines', 'firmwares', 'machine_search_key', 'area_search_key', 'account_search_key', 'firmware_search_key']
    );
    const pathname = this.props.location.pathname;

    history.replace({
      pathname,
      search: queryString
    });
  }, 100);

  getPageColumns = (props) => {
    const deviceListSettings = JSON.parse(localStorage.getItem('device_list_settings'));
    if (props.location.pathname === '/admin-dashboard/devices' ||
      !deviceListSettings || !deviceListSettings.columns
    ) {
      return [...defaultSensorsPageColumns];
    }
    return deviceListSettings.columns;
  }

  selectFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(this.props.location.search);
    const searchParams = {
      ...this.initialSearchParams
    };
    Object.keys(searchParams).forEach((key) => {
      if (urlParams.get(key)) {
        searchParams[key] = isJsonString(urlParams.get(key)) ?
          JSON.parse(urlParams.get(key)) :
          urlParams.get(key);
      }
    });

    const currentSearchKeys = {
      machine_search_key: this.state.searchParams.machine_search_key,
      area_search_key: this.state.searchParams.area_search_key,
      account_search_key: this.state.searchParams.account_search_key,
      firmware_search_key: this.state.searchParams.firmware_search_key
    };

    const finalSearchParams = {
      ...searchParams,
      ...currentSearchKeys
    };
    if (!_.isEqual(this.state.searchParams, finalSearchParams)) {
      this.setState({ searchParams: finalSearchParams });
      this.search(finalSearchParams);
    }
  }

  selectFilterFromCharts = (newFilter = null) => {
    let filter;
    if (newFilter) filter = newFilter;
    else if (this.props.location.state && this.props.location.state.filter) filter = this.props.location.state.filter;
    else return;

    let searchParams = {
      ...this.initialSearchParams
    };
    const filters = {};

    filter.forEach((f) => {
      if (f.op === 'in') filters[f.name] = f.value;
      if (f.op === 'not_in' && f.allDropdownsKey) {
        const allDropdowns = this.state[f.allDropdownsKey];
        filters[f.name] = allDropdowns.filter(item => !f.value.includes(item.value)).map(item => item.value);
      }
    });
    searchParams = {
      ...searchParams,
      ...filters
    };

    this.setState({ searchParams }, this.updateUrlQueryString);
    this.search(searchParams);
  }

  clearAllFilters = () => {
    const searchParams = {
      ...this.initialSearchParams
    };
    if (this.state.machineSensors) {
      searchParams.machines = this.state.searchParams.machines;
    }
    this.setState({ searchParams }, this.updateUrlQueryString);
    if (this.state.dateExpanded) this.toggleDateExpanded();
    this.search(searchParams);
  }

  removeAppliedFilters = (serial_number) => {
    const { machine_id } = this.props.match.params;
    const { setSensorsFilters } = this.props.sensorActions;
    this.setState(
      {
        searchParams: _.cloneDeep(this.initialSearchParams)
      },
      () => {
        let p1;
        if (machine_id) {
          p1 = this.changeFilter('machines', [Number(machine_id)]);
        } else {
          const filters = [
            { name: 'machine_id', op: 'in', value: [] },
            { name: 'account_id', op: 'in', value: [] },
            { name: 'battery_level', op: 'in', value: [] },
            { name: 'type', op: 'in', value: [] },
            { name: 'wifi_signal', op: 'in', value: [] },
            { name: 'area_id', op: 'in', value: [] },
            { name: 'unreachable', op: 'in', value: [] },
            { name: 'mcu_firmware', op: 'in', value: [] },
            { name: 'last_measurement_from', op: 'ge', value: '' },
            { name: 'last_measurement_to', op: 'le', value: '' },
            { name: 'unreachable_extended', op: 'eq', value: '' },
            { name: 'other_filters', op: 'in', value: [] }
          ];
          p1 = setSensorsFilters(filters, '');
          this.updateUrlQueryString();
        }
        Promise.all([p1]).then(() => {
          const { getSensorDetails } = this.props.sensorActions;
          getSensorDetails(serial_number, true);
        });
      }
    );
  };

  search = (params = null) => {
    const { searchParams: stateParams } = this.state;
    const searchParams = params || stateParams;
    const { setSensorsFilters } = this.props.sensorActions;
    let unreachable = [];
    let unreachable_extended_value = '';
    if (searchParams.last_measurement.includes(true) || searchParams.last_measurement.includes(false)) {
      unreachable = searchParams.last_measurement;
    }
    if (searchParams.last_measurement.includes('unreachable_extended')) {
      unreachable_extended_value = 14;
    }
    let wifi_signal = [];
    wifi_signal = [...searchParams.wifi_signal];
    if (wifi_signal.includes(4)) {
      wifi_signal.push(5);
      // both wifi_signal level 4,5 are mapped to Strong
    }
    const filters = [
      { name: 'machine_id', op: 'in', value: searchParams.machines },
      { name: 'account_id', op: 'in', value: searchParams.accounts },
      { name: 'battery_level', op: 'in', value: searchParams.battery_level },
      { name: 'type', op: 'in', value: searchParams.type },
      { name: 'wifi_signal', op: 'in', value: wifi_signal },
      { name: 'area_id', op: 'in', value: searchParams.area },
      { name: 'unreachable', op: 'in', value: unreachable },
      { name: 'last_measurement_from', op: 'ge', value: searchParams.last_measurement_from },
      { name: 'last_measurement_to', op: 'le', value: searchParams.last_measurement_to },
      { name: 'mcu_firmware', op: 'in', value: searchParams.firmwares },
      { name: 'unreachable_extended', op: 'eq', value: unreachable_extended_value },
      { name: 'other_filters', op: 'in', value: searchParams.other_filters }
    ];
    this.setState({
      devices_health_widget_filters: { name: 'account_id', op: 'in', value: searchParams.accounts }
    });
    let last_measurement_filter = null;
    if (searchParams.last_measurement.includes('assigned')) last_measurement_filter = 'assigned';
    this.updateDeviceListLocalStorage('last_measurement_filter', last_measurement_filter);

    if (searchParams.last_measurement.includes('unassigned')) filters.push({ name: 'machine', op: 'eq', value: '' });
    else if (searchParams.last_measurement.includes(true) || searchParams.last_measurement.includes(false) ||
      searchParams.last_measurement.includes('assigned') || searchParams.last_measurement.includes('unreachable_extended') ||
      searchParams.other_filters.includes('devices_with_continuous_outliers')) filters.push({ name: 'machine', op: 'ne', value: '' });
    return setSensorsFilters(filters, searchParams.search_key);
  };

  updateDeviceListLocalStorage = (key, value) => {
    let deviceListSettings = JSON.parse(localStorage.getItem('device_list_settings'));
    deviceListSettings = {
      ...deviceListSettings,
      [key]: value
    };
    localStorage.setItem('device_list_settings', JSON.stringify(deviceListSettings));
  }

  toggleFilterOpen = (filter) => {
    this.setState(prevState => ({
      filtersOpen: {
        ...prevState.filtersOpen,
        [filter]: !prevState.filtersOpen[filter]
      }
    }));
  };

  toggleDateExpanded = () => {
    this.setState(prevState => ({
      dateExpanded: !prevState.dateExpanded
    }));
  };

  clearDate = () => {
    this.setState(prevState => ({
      searchParams: {
        ...prevState.searchParams,
        last_measurement_from: '',
        last_measurement_to: ''
      },
      customDayFrom: false,
      customDayTo: false
    }), () => {
      this.search(this.state.searchParams);
    });
  };

  closeCalender = () => {
    this.setState({ calenderOpen: null });
  };

  openCustomCalender = (str) => {
    this.setState({
      calenderOpen: str
    });
  };

  onClickDay = (value, label) => {
    let searchParams = {};
    if (label === 'from') {
      searchParams = {
        ...this.state.searchParams,
        last_measurement_from: value
      };
      this.setState({
        searchParams,
        customDayFrom: value,
        calenderOpen: null
      });
    } else if (label === 'to') {
      searchParams = {
        ...this.state.searchParams,
        last_measurement_to: value
      };
      this.setState({
        searchParams,
        customDayTo: value,
        calenderOpen: null
      });
    }
    this.search(searchParams);
  };

  changeFilter = (filter, value) => {
    this.setState(prevState => ({
      searchParams: {
        ...prevState.searchParams,
        [filter]: value
      }
    }), () => {
      this.updateUrlQueryString();
      const { searchParams } = this.state;
      if (filter === 'search_key') {
        this.nameSearchDebounce(searchParams);
      } else {
        this.search(searchParams);
      }
    });
  };

  changeLastMeasurementFilter = (filter, value) => {
    this.setState(prevState => ({
      searchParams: {
        ...prevState.searchParams,
        [filter]: value,
        last_measurement_from: '',
        last_measurement_to: ''
      },
      customDayFrom: false,
      customDayTo: false
    }), () => {
      this.updateUrlQueryString();
      this.search(this.state.searchParams);
    });
  };

  closeDetails = () => {
    const { clearSelectedSensor } = this.props.sensorActions;
    clearSelectedSensor();
  };

  nameSearchDebounce = _.debounce((params) => {
    this.search(params);
  }, 400);

  renderFilterOptions = (options, name) => {
    if (_.isEmpty(options)) {
      return (
        <MsgContainer>
          <span>{this.props.t('sensors.filterEmpty')}</span>
        </MsgContainer>
      );
    }

    return options.map((option, index) => {
      const selected = _.includes(this.state.searchParams[name], option.value);
      if (name === 'last_measurement') {
        return (
          <FilterItem
            key={index.toString()}
            className="dropdown-item"
            selected={selected}
            onClick={() => {
              let types = [];
              if (!selected) {
                types = [option.value];
              }
              if (option.value === 'custom' || this.state.dateExpanded) {
                this.toggleDateExpanded('dateExpanded');
              }
              this.changeLastMeasurementFilter(name, types, selected);
            }}
          >
            <Radiobutton value={selected} />
            <span>{option.text}</span>
          </FilterItem>
        );
      }
      if (name === 'other_filters') {
        return (
          <FilterItem
            key={index.toString()}
            className="dropdown-item"
            selected={selected}
            onClick={() => {
              let types = [];
              if (!selected) {
                types = [option.value];
              }
              this.changeFilter(name, types, selected);
            }}
          >
            <Radiobutton value={selected} />
            <span>{option.text}</span>
          </FilterItem>
        );
      }
      const optionLabel = name === 'type'
        ? this.getSensorTypeLabel(option.text)
        : (option.name || option.text);
      return (
        <FilterItem
          key={index.toString()}
          className="dropdown-item"
          selected={selected}
          onClick={() => {
            let types = [];
            if (selected) {
              types = _.filter(
                this.state.searchParams[name],
                t => t !== option.value
              );
            } else {
              types = [...this.state.searchParams[name], option.value];
            }
            this.changeFilter(name, types, selected);
          }}
        >
          <Checkbox value={selected} />
          <span>{optionLabel}</span>
        </FilterItem>
      );
    });
  };

  searchMachineFilter = (value) => {
    this.setState(prevState => ({
      machines: prevState.allMachines.filter(u =>
        u.text.toLowerCase().includes(value.toLowerCase())
      ),
      searchParams: {
        ...prevState.searchParams,
        machine_search_key: value
      }
    }));
  };

  searchAreaFilter = (value) => {
    this.setState(prevState => ({
      area: prevState.allArea.filter(u =>
        u.text.toLowerCase().includes(value.toLowerCase())
      ),
      searchParams: {
        ...prevState.searchParams,
        area_search_key: value
      }
    }));
  };

  searchAccountFilter = (value) => {
    this.setState(prevState => ({
      accounts: prevState.allAccounts.filter(u =>
        u.text.toLowerCase().includes(value.toLowerCase())
      ),
      searchParams: {
        ...prevState.searchParams,
        account_search_key: value
      }
    }));
  };

  searchFirmwareFilter = (value) => {
    this.setState(prevState => ({
      firmwares: prevState.allFirmwares.filter(u =>
        u.text.toLowerCase().includes(value.toLowerCase())
      ),
      searchParams: {
        ...prevState.searchParams,
        firmware_search_key: value
      }
    }));
  }

  toggleAssignModel = () => {
    this.setState(prevState => ({
      assignModelOpen: !prevState.assignModelOpen
    }));
  }

  toggleUnassignModel = () => {
    this.setState(prevState => ({
      unassignModelOpen: !prevState.unassignModelOpen
    }));
  }

  toggleAddDeviceModel = () => {
    this.setState(prevState => ({
      addDeviceModelOpen: !prevState.addDeviceModelOpen
    }));
  }

  unassignDevice = () => {
    const {
      items,
      selectedSensorList
    } = this.props.sensors;
    const serial_numbers = [];
    items.object.filter(item => selectedSensorList.includes(item.serial_number) && item.account_id !== null).forEach(item => serial_numbers.push(item.serial_number));
    const params = {
      account_id: null,
      serial_numbers
    };
    this.props.sensorActions.assignDevicesToAccount(params).then(() => {
      this.search();
    });
    this.toggleUnassignModel();
  }

  assignDevicesToAccount = (account_id) => {
    const {
      items,
      selectedSensorList
    } = this.props.sensors;
    const serial_numbers = [];
    items.object.filter(item => selectedSensorList.includes(item.serial_number) && item.account_id === null).forEach(item => serial_numbers.push(item.serial_number));
    const params = {
      account_id,
      serial_numbers
    };
    this.props.sensorActions.assignDevicesToAccount(params).then(() => {
      this.search();
    });
    this.toggleAssignModel();
  }

  addDevice = (device_type, account_id, serial_number, device_id, mac_address, reporting_interval, tx_model_id, mote_model_id) => {
    let params = {};
    switch (device_type) {
      case 'Transmitter':
        params = {
          account_id,
          serial_number,
          device_id,
          mac_address,
          reporting_interval,
          tx_model_id
        };
        this.props.sensorActions.createTx(params).then(() => {
          toastr.success(this.props.t('sensors.messages.transmitterCreated'));
          this.search();
        }).catch((e) => {
          toastr.error(e.toString());
        });
        break;
      case 'Mote':
        params = {
          account_id,
          serial_number,
          device_id,
          mac_address,
          mote_model_id
        };
        this.props.sensorActions.createMote(params).then(() => {
          toastr.success(this.props.t('sensors.messages.moteCreated'));
          this.search();
        }).catch((e) => {
          toastr.error(e.toString());
        });
        break;
      default:
        break;
    }
    this.toggleAddDeviceModel();
  }

  getChartFilters = () => {
    const filters = [];
    if (_.isEmpty(this.state.searchParams.accounts) && this.props.adminDashboard.adminOpen) {
      filters.push({ name: 'all', op: 'eq', value: true });
    }
    if (!_.isEmpty(this.state.searchParams.accounts)) {
      filters.push({ name: 'account_id', op: 'in', value: this.state.searchParams.accounts });
    }
    return filters;
  };

  handleRowClick = (item) => {
    const {
      selectSensor, getSensorDetails, clearDetails, selectGivenSensor
    } = this.props.sensorActions;
    const { serial_number, model, type } = item;
    const { selectedList } = this.props;
    const { editMode } = this.state;
    const adminOpen = this.props.adminDashboard.adminOpen;
    const details = this.props.sensors.details.details;
    const modelKey = [...StandardSensors, ...VectorSensors].includes(type) ? model : 'other';
    if (editMode || adminOpen) {
      if (selectedList.includes(serial_number)) {
        clearDetails(modelKey, serial_number);
      } else {
        if (!adminOpen) getSensorDetails(serial_number);
        selectSensor(serial_number);
      }
    } else {
      const alreadyLoaded = details[modelKey]
        && details[modelKey].findIndex(s => s.serial_number === serial_number) !== -1;
      if (!alreadyLoaded) getSensorDetails(serial_number);
      selectGivenSensor(serial_number);
    }
  };

  getFilterTexts = () => {
    const sp = this.state.searchParams;
    const reduceNames = (ids, lookup) => ids.reduce((acc, id, idx) => {
      const match = lookup(id);
      const label = match || '';
      if (idx === 0) return `${label}`;
      if (idx === 1) return `${acc}, ${label}...`;
      return acc;
    }, '');

    const machine = this.state.machineSensors ? '' : reduceNames(sp.machines, (id) => {
      const m = this.state.allMachines.find(x => x.value === id);
      return m && m.text;
    });
    const area = this.state.machineSensors ? '' : reduceNames(sp.area, (id) => {
      const a = this.state.allArea.find(x => x.value === id);
      return a && a.text;
    });
    const account = reduceNames(sp.accounts, (id) => {
      const a = this.state.allAccounts.find(x => x.value === id);
      return a && a.text;
    });
    const firmware = sp.firmwares.reduce((acc, id, idx) => {
      if (idx === 0) return `${id}`;
      if (idx === 1) return `${acc}, ...`;
      return acc;
    }, '');
    const type = reduceNames(sp.type, (id) => {
      const m = this.state.typeOptions.find(x => x.value === id);
      return m && this.getSensorTypeLabel(m.text);
    });
    const wifi = reduceNames(sp.wifi_signal, (id) => {
      const w = this.getWifiSignalOptions().find(s => s.value === id);
      return w && w.name;
    });
    const battery = sp.battery_level.reduce((acc, id, idx) => {
      const b = this.getBatteryLevelOptions().find(s => s.value === id);
      const label = b ? b.name : '';
      if (idx === 0) return `${label}`;
      return `${acc}, ${label}`;
    }, '');
    const otherFilters = sp.other_filters.reduce((acc, id, idx) => {
      const o = this.getOtherFiltersOptions().find(s => s.value === id);
      const label = o ? o.name : '';
      if (idx === 0) return `${label}`;
      return `${acc}, ${label}`;
    }, '');
    const lastMeasurement = reduceNames(sp.last_measurement, (id) => {
      const l = this.getLastMeasurementOptions().find(s => s.value === id);
      return l && l.name;
    });
    return {
      machine, area, account, firmware, type, wifi, battery, otherFilters, lastMeasurement
    };
  };

  getFilterFields = () => {
    const { t } = this.props;
    const adminOpen = this.props.adminDashboard.adminOpen;
    const fields = [{ name: 'type', title: t('devices.filters.type') }];
    if (!adminOpen && !this.state.machineSensors) {
      fields.push({ name: 'area', title: t('devices.filters.area') });
      fields.push({ name: 'machine', title: t('devices.filters.machine') });
    }
    fields.push({ name: 'battery_level', title: t('devices.filters.battery_level') });
    fields.push({ name: 'wifi_signal', title: t('devices.filters.wifi_signal') });
    fields.push({ name: 'last_reported', title: t('devices.filters.last_reported') });
    fields.push({ name: 'firmware', title: t('devices.filters.firmware') });
    if (adminOpen) fields.push({ name: 'account', title: t('devices.filters.account') });
    fields.push({ name: 'other_filters', title: t('devices.filters.other_filters') });
    return fields;
  };

  getFilterFieldMeta = (field) => {
    switch (field) {
      case 'area':
        return {
          key: 'area',
          options: this.state.area,
          searchable: true,
          onSearch: this.searchAreaFilter,
          searchValue: this.state.searchParams.area_search_key
        };
      case 'machine':
        return {
          key: 'machines',
          options: this.state.machines,
          searchable: true,
          onSearch: this.searchMachineFilter,
          searchValue: this.state.searchParams.machine_search_key
        };
      case 'battery_level':
        return { key: 'battery_level', options: this.getBatteryLevelOptions() };
      case 'wifi_signal':
        return { key: 'wifi_signal', options: this.getWifiSignalOptions() };
      case 'last_reported':
        return { key: 'last_measurement', options: this.getLastMeasurementOptions() };
      case 'firmware':
        return {
          key: 'firmwares',
          options: this.state.firmwares,
          searchable: true,
          onSearch: this.searchFirmwareFilter,
          searchValue: this.state.searchParams.firmware_search_key
        };
      case 'account':
        return {
          key: 'accounts',
          options: this.state.accounts,
          searchable: true,
          onSearch: this.searchAccountFilter,
          searchValue: this.state.searchParams.account_search_key
        };
      case 'other_filters':
        return { key: 'other_filters', options: this.getOtherFiltersOptions() };
      case 'type':
      default:
        return { key: 'type', options: this.state.typeOptions };
    }
  };

  renderCustomDate = () => (
    <DateSelector>
      {this.state.calenderOpen === 'from' && (
        <OutsideAlerter open={this.state.calenderOpen === 'from'} handleClick={this.closeCalender}>
          <CalenderContainer>
            <Calendar
              formatShortWeekday={calendarFormatDay}
              maxDetail="month"
              maxDate={this.state.customDayTo || new Date()}
              showNeighboringMonth={false}
              onClickDay={value => this.onClickDay(value, 'from')}
              value={this.state.customDayFrom}
            />
          </CalenderContainer>
        </OutsideAlerter>
      )}
      {this.state.calenderOpen === 'to' && (
        <OutsideAlerter open={this.state.calenderOpen === 'to'} handleClick={this.closeCalender}>
          <CalenderContainer to="true">
            <Calendar
              formatShortWeekday={calendarFormatDay}
              maxDetail="month"
              maxDate={new Date()}
              minDate={this.state.customDayFrom}
              value={this.state.customDayTo}
              showNeighboringMonth={false}
              onClickDay={value => this.onClickDay(value, 'to')}
            />
          </CalenderContainer>
        </OutsideAlerter>
      )}
      <FlexContainer direction="row" justifyContent="space-between">
        <H2>{this.props.t('devices.filterOptions.lastMeasurement.custom')}</H2>
        <SvgContainer onClick={this.toggleDateExpanded}>
          <CrossSvg width={30} height={30} fill="#999B95" />
        </SvgContainer>
      </FlexContainer>
      <InputField label={this.props.t('devices.filterOptions.customDate.from')} placeholder={this.props.t('devices.filterOptions.customDate.select')} readOnly value={this.state.customDayFrom ? this.state.customDayFrom.toString().slice(0, 15) : ''} onFocus={() => this.openCustomCalender('from')} />
      <InputField label={this.props.t('devices.filterOptions.customDate.to')} placeholder={this.props.t('devices.filterOptions.customDate.select')} readOnly value={this.state.customDayTo ? this.state.customDayTo.toString().slice(0, 15) : ''} onFocus={() => this.openCustomCalender('to')} />
      <Button text onClick={this.clearDate} disabled={!this.state.customDayFrom && !this.state.customDayTo}>
        {this.props.t('devices.filterOptions.customDate.clear')}
      </Button>
    </DateSelector>
  );

  renderFieldOptions = (field) => {
    const meta = this.getFilterFieldMeta(field);
    return (
      <div data-testid="device-filter-option-panel">
        {meta.searchable && (
          <OptionSearch>
            <input
              type="text"
              value={meta.searchValue}
              placeholder={this.props.t('devices.searchPlaceholder')}
              onChange={e => meta.onSearch(e.target.value)}
            />
          </OptionSearch>
        )}
        {this.renderFilterOptions(meta.options, meta.key)}
        {field === 'last_reported' && this.state.dateExpanded && this.renderCustomDate()}
      </div>
    );
  };

  getAppliedChips = () => {
    const { t } = this.props;
    const sp = this.state.searchParams;
    const texts = this.getFilterTexts();
    const chips = [];
    if (sp.type.length) chips.push({ name: 'type', title: t('devices.filters.type'), text: texts.type });
    if (!this.state.machineSensors && sp.area.length) chips.push({ name: 'area', title: t('devices.filters.area'), text: texts.area });
    if (!this.state.machineSensors && sp.machines.length) chips.push({ name: 'machines', title: t('devices.filters.machine'), text: texts.machine });
    if (sp.battery_level.length) chips.push({ name: 'battery_level', title: t('devices.filters.battery_level'), text: texts.battery });
    if (sp.wifi_signal.length) chips.push({ name: 'wifi_signal', title: t('devices.filters.wifi_signal'), text: texts.wifi });
    if (sp.last_measurement.length) chips.push({ name: 'last_measurement', title: t('devices.filters.last_reported'), text: texts.lastMeasurement });
    if (sp.firmwares.length) chips.push({ name: 'firmwares', title: t('devices.filters.firmware'), text: texts.firmware });
    if (this.props.adminDashboard.adminOpen && sp.accounts.length) chips.push({ name: 'accounts', title: t('devices.filters.account'), text: texts.account });
    if (sp.other_filters.length) chips.push({ name: 'other_filters', title: t('devices.filters.other_filters'), text: texts.otherFilters });
    return chips;
  };

  removeChip = (name) => {
    if (name === 'last_measurement') this.changeLastMeasurementFilter('last_measurement', []);
    else this.changeFilter(name, []);
  };

  render() {
    const {
      sorter,
      items,
      selectedSensorList
    } = this.props.sensors;

    const primaryColor = this.props.primaryColor;
    const sensorsMetadataLoading = sensorsConstants.GET_SENSORS_METADATA in this.props.loadingReducer ?
      this.props.loadingReducer[sensorsConstants.GET_SENSORS_METADATA] : true;

    const { currentAccount, widgetsMetadata } = this.props;
    const { setSensorsSorter, loadMoreSensors } = this.props.sensorActions;
    const { selectedColumns } = this.state;
    const unassignSensors = items.object.filter(
      item => selectedSensorList.includes(item.serial_number) && item.account_id !== null
    );
    const adminOpen = this.props.adminDashboard.adminOpen;
    let deviceColumns = [...selectedColumns];
    let sortDisabled = false;
    if (adminOpen) {
      deviceColumns = [...selectedColumns, 'account_id'];
    }
    if (selectedSensorList.length > 0 && !adminOpen) {
      deviceColumns = ['serial_number'];
      sortDisabled = true;
    }

    const { machine_id } = this.props.match.params;
    const chartFilters = this.getChartFilters();
    const showDeviceInsights = !this.state.machineSensors && selectedSensorList.length === 0;

    return (
      <div id="devices-page">
        <MoreOptions>
          <AccountMenuItems />
        </MoreOptions>
        <SearchFilters machineSensors={this.state.machineSensors}>
          <FlexContainer className="filter-misc-items" alignItems="center" style={{ width: '100%' }}>
            {items && (
              <FilterCountContainer>{items.total_count}</FilterCountContainer>
            )}
            <FlexContainer alignItems="center" style={{ marginLeft: 'auto', gap: '8px' }}>
              <EditDropdown
                title={this.props.t('devices.selectColumnsTitle')}
                labelKeyPrefix="devices.headers"
                allColumns={allSensorsPageColumns}
                selectedColumns={this.state.selectedColumns}
                toggleColumn={column => this.setState(prevState => (
                  prevState.selectedColumns.includes(column) ? ({
                    selectedColumns: prevState.selectedColumns.filter(item => item !== column)
                  }) : ({
                    // done this way to maintain order
                    selectedColumns: allSensorsPageColumns.filter(item => (
                      prevState.selectedColumns.includes(item) ||
                      item === column
                    ))
                  })
                ), () => this.updateDeviceListLocalStorage('columns', this.state.selectedColumns))}
                renderTrigger={open => (
                  <GhostBtn type="button" onClick={open} title={this.props.t('devices.editColumns')}>
                    <ColumnIcon />
                    {this.props.t('devices.columns', { defaultValue: 'Columns' })}
                  </GhostBtn>
                )}
              />
              <ToolbarSearchBox
                name="device-search-box"
                placeholder={this.props.t('devices.searchPlaceholder')}
                onChange={e => this.changeFilter('search_key', e.target.value)}
                value={this.state.searchParams.search_key}
                clearLabel={this.props.t('common.clear')}
              />
              {!this.props.adminDashboard.adminOpen && (
                <RBAC
                  resource={mapComponentToResource.Sensors}
                  operation={operations.Update}
                  yes={(
                    <GhostBtn
                      type="button"
                      title={this.props.t('devices.iconTitles.editSensors')}
                      onClick={() => {
                        this.setState(prevState =>
                          ({ editMode: !prevState.editMode, itemMenuOpen: false }));
                        if (this.props.selectedList.length === 1) return;
                        this.closeDetails();
                      }}
                    >
                      <EditIcon fill={this.state.editMode ? primaryColor : colors.greenXD} />
                    </GhostBtn>
                  )}
                />
              )}
              {this.props.adminDashboard.adminOpen && !isPartnerAdmin(this.props.user) && (
                <RBAC
                  resource={mapComponentToResource.Sensors}
                  operation={operations.Create}
                  yes={(
                    <PrimaryButton
                      type="button"
                      title={this.props.t('devices.iconTitles.addDevice')}
                      onClick={this.toggleAddDeviceModel}
                    >
                      <Plus />
                      {this.props.t('devices.addDevice.title', { defaultValue: 'Add Device' })}
                    </PrimaryButton>
                  )}
                />
              )}
              {!this.props.adminDashboard.adminOpen && (
                <RBAC
                  resource={mapComponentToResource.Sensors}
                  operation={operations.Create}
                  yes={(
                    <PrimaryLinkBtn
                      title={this.props.t('devices.iconTitles.createNewSensors')}
                      to={Routes.createNewSensors}
                    >
                      <Plus />
                      {this.props.t('devices.addDevice.title', { defaultValue: 'Add Device' })}
                    </PrimaryLinkBtn>
                  )}
                />
              )}
            </FlexContainer>
          </FlexContainer>
        </SearchFilters>
        <FilterBar
          labels={{
            addFilter: this.props.t('devices.filtersButton', { defaultValue: 'Filter' }),
            clearAll: this.props.t('devices.clearFilters', { defaultValue: 'Clear all' })
          }}
          testIds={{ addButton: 'device-filter-add-button', fieldOption: 'device-filter-field-option' }}
          filterFields={this.getFilterFields()}
          renderFieldOptions={this.renderFieldOptions}
          chips={this.getAppliedChips()}
          onRemoveChip={this.removeChip}
          onClearAll={this.clearAllFilters}
          showInsights={showDeviceInsights}
          insightsOpen={this.state.insightsOpen}
          onToggleInsights={this.toggleInsights}
        />
        {!currentAccount.error && showDeviceInsights && this.state.insightsOpen && (
          <DevicesInsights
            widgetsMetadata={widgetsMetadata}
            chartFilters={chartFilters}
            title={this.props.t('devices.insights.title', { defaultValue: 'Insights' })}
            hint={this.props.t('devices.insights.hint', { defaultValue: 'Drag to rearrange' })}
          />
        )}
        {currentAccount.error && (
          <div>{currentAccount.error}</div>
        )}
        {!currentAccount.error && (
          <>
            {sensorsMetadataLoading ? (
              <SensorsContainer>
                <Loading position="absolute" style={{ top: 0, left: 0 }} height="100%" />
              </SensorsContainer>
            ) : (
              <SensorsContainer>
                <ListContainer
                  width={selectedSensorList.length > 0 && !this.props.adminDashboard.adminOpen ? '30%' : '100%'}
                  minWidth={selectedSensorList.length === 0 && '45rem'}
                >
                  <DevicesTable
                    columns={deviceColumns}
                    headerLabel={name => this.props.t(`devices.headers.${name}`)}
                    rows={items.object}
                    sorter={sorter}
                    setSorter={setSensorsSorter}
                    sortDisabled={sortDisabled}
                    onRowClick={this.handleRowClick}
                    selectedList={selectedSensorList}
                    editMode={this.state.editMode}
                    adminOpen={adminOpen}
                    loading={items.loading || currentAccount.fetching}
                    hasMore={items.hasMore}
                    loadMoreItems={loadMoreSensors}
                    emptyMsg={this.props.t('devices.emptyMsg')}
                    scrollToRowKey={!this.state.editMode && selectedSensorList.length ? selectedSensorList[0] : null}
                    t={this.props.t}
                  />
                </ListContainer>
                {selectedSensorList.length > 0 && !this.props.adminDashboard.adminOpen && (
                  <SensorsDetails
                    sensorsList={items.object.filter(item =>
                      selectedSensorList.includes(item.serial_number)
                    )}
                    closeDetails={this.closeDetails}
                    editMode={this.state.editMode}
                    machines={this.state.allMachines}
                    machineSensors={this.state.machineSensors}
                    machine_id={parseInt(machine_id, 10)}
                    filters={this.state.filtersOpen}
                    searchParams={this.state.searchParams}
                    version={
                      currentAccount.preferences &&
                      currentAccount.preferences.measurement_version
                    }
                    utcOffset={
                      currentAccount.preferences &&
                      currentAccount.preferences.timezone.utc_offset
                    }
                    appliedFilters={_.cloneDeep(this.state.searchParams)}
                    removeAppliedFilters={this.removeAppliedFilters}
                    onReassignComplete={this.search}
                  />
                )}
              </SensorsContainer>
            )}
          </>
        )}
        {selectedSensorList.length > 0 && this.props.adminDashboard.adminOpen && (
          <FooterFlex
            justifyContent="space-between"
            width={this.props.hierarchyView ? '74.9%' : '100%'}
          >
            <FlexContainer>
              <Label fontSize="14px"> {selectedSensorList.length} &nbsp;</Label>
              <Label fontSize="14px" fontWeight="300">
                {' '}
                {selectedSensorList.length === 1
                  ? this.props.t('sensors.footer.deviceSelected')
                  : this.props.t('sensors.footer.devicesSelected')}
              </Label>
            </FlexContainer>
            <FlexContainer>
              <RBAC
                resource={mapComponentToResource.Sensors}
                operation={operations.Update}
                yes={(
                  <>
                    {items.object.filter(item =>
                      selectedSensorList.includes(item.serial_number) && item.account_id === null
                    ).length > 0 && (
                        <Button onClick={this.toggleAssignModel} secondary="#000">{this.props.t('sensors.footer.assignToAccount')}</Button>
                    )}
                    {items.object.filter(item =>
                      selectedSensorList.includes(item.serial_number) && item.account_id !== null
                    ).length > 0 && (
                        <Button onClick={this.toggleUnassignModel} secondary="#000">{this.props.t('sensors.footer.unassignDevice')}</Button>
                    )}
                  </>
                )}
              />
            </FlexContainer>
          </FooterFlex>
        )}
        {this.state.assignModelOpen && (
            <AssignModel
              close={this.toggleAssignModel}
              assign={this.assignDevicesToAccount}
              text={
              items.object.filter(item => selectedSensorList.includes(item.serial_number) && item.account_id === null).length === 1
                ?
                items.object.filter(item => selectedSensorList.includes(item.serial_number) && item.account_id === null)[0].serial_number
                :
                this.props.t('sensors.assignModal.devicesCount', {
                  count: items.object.filter(item => selectedSensorList.includes(item.serial_number) && item.account_id === null).length
                })
            }
            />
        )}
        {this.state.unassignModelOpen && (
          <UnassignSensorAlertPrompt
            message={
              unassignSensors.length === 1 ?
                this.props.t('sensors.unassignConfirm.single', {
                  serial: unassignSensors[0].serial_number,
                  defaultValue: `Are you sure you want to unassign ${unassignSensors[0].serial_number} ?`
                })
                :
                this.props.t('sensors.unassignConfirm.multiple', {
                  count: unassignSensors.length,
                  defaultValue: `Are you sure you want to unassign ${unassignSensors.length} devices ?`
                })
            }
            onCancel={this.toggleUnassignModel}
            onProceed={this.unassignDevice}
          />
        )}
        {this.state.addDeviceModelOpen && (
          <AddDeviceModel
            close={this.toggleAddDeviceModel}
            add={this.addDevice}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  sensors: state.sensors,
  currentAccount: state.currentAccount,
  user: state.user.user,
  SensorModels: state.settings.SensorModels,
  selectedList: state.sensors.selectedSensorList,
  adminDashboard: state.adminDashboard,
  widgetsMetadata: state.widgets.metadata,
  loadingReducer: state.loadingReducer,
  primaryColor: state.companyReducer.partner.theme.primaryColor,
  title: state.companyReducer.partner.title
});

const mapDispatchToProps = dispatch => ({
  sensorActions: bindActionCreators(sensorActions, dispatch),
  machineDetailsActions: bindActionCreators(machineDetailsActions, dispatch),
  modelActions: bindActionCreators(modelActions, dispatch),
  widgetActions: bindActionCreators(widgetActions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withRouter(withTheme(Sensors))));
